import { getProviderInstance, addBreadcrumb, obfuscateEmail } from './chunk-Z2R6UR6G.js';

// core/helpers.ts
function captureProviderError(error, options) {
  const provider = getProviderInstance();
  if (!provider) return;
  addBreadcrumb(
    `provider.${options.type}`,
    `${options.provider} ${options.operation} failed`,
    {
      provider: options.provider,
      operation: options.operation,
      errorCode: options.providerErrorCode
    },
    "error"
  );
  const context = {
    level: options.level || "error",
    tags: {
      provider: options.provider,
      provider_operation: options.operation,
      provider_type: options.type,
      ...options.providerErrorCode && { provider_error_code: options.providerErrorCode },
      ...options.tags
    },
    extra: {
      providerContext: {
        provider: options.provider,
        operation: options.operation,
        type: options.type,
        errorCode: options.providerErrorCode,
        errorMessage: options.providerErrorMessage
      },
      ...options.context
    }
  };
  provider.captureException(
    error instanceof Error ? error : new Error(String(error)),
    context
  );
}
function serializeCause(cause) {
  if (typeof cause === "string") return cause;
  try {
    const json = JSON.stringify(cause);
    if (json && json !== "{}") return json;
  } catch {
  }
  return String(cause);
}
function reportClientPaymentError(result, { surface, operation, provider = "stripe", context, tags, cause }) {
  const isInfra = !!result.isInfrastructureError;
  const kind = isInfra ? "infrastructure" : result.code || result.errorType ? "card_error" : "incomplete";
  const reported = cause instanceof Error ? cause : new Error(result.error ?? `${operation} failed`);
  const rawCause = cause !== void 0 && !(cause instanceof Error) ? { rawCause: serializeCause(cause) } : void 0;
  const mergedContext = context || rawCause ? { ...context, ...rawCause } : void 0;
  captureProviderError(reported, {
    provider,
    operation,
    type: "payment",
    level: isInfra ? "error" : "warning",
    providerErrorCode: result.code,
    providerErrorMessage: result.error,
    tags: {
      // Caller tags first — the derived ones below must win.
      ...tags,
      surface,
      client_side: true,
      payment_error_kind: kind,
      ...result.errorType ? { stripe_error_type: result.errorType } : {}
    },
    ...mergedContext ? { context: mergedContext } : {}
  });
}
function captureSecurityEvent(options) {
  const provider = getProviderInstance();
  if (!provider) return;
  addBreadcrumb(
    "security",
    `Security event: ${options.event}`,
    {
      event: options.event,
      severity: options.severity,
      affectedUserCount: options.affectedUsers?.length || 0
    },
    options.severity === "critical" || options.severity === "high" ? "error" : "warning"
  );
  const context = {
    level: options.severity === "critical" ? "fatal" : options.severity === "high" ? "error" : options.severity === "medium" ? "warning" : "info",
    tags: {
      security_event: options.event,
      security_severity: options.severity,
      affected_user_count: options.affectedUsers?.length || 0
    },
    extra: {
      securityDetails: options.details,
      affectedUsers: options.affectedUsers
    },
    security: {
      event: options.event,
      severity: options.severity,
      affectedUsers: options.affectedUsers,
      details: options.details
    }
  };
  const error = new Error(`Security Event: ${options.event}`);
  error.name = "SecurityEvent";
  provider.captureException(error, context);
}
function capturePaymentFailure(error, details) {
  const provider = getProviderInstance();
  if (!provider) return;
  addBreadcrumb(
    "payment",
    `Payment failure: ${details.provider}`,
    {
      provider: details.provider,
      amount: details.amount,
      currency: details.currency,
      bookingId: details.bookingId
    },
    "error"
  );
  const context = {
    level: "error",
    tags: {
      payment_provider: details.provider,
      alert_priority: "critical",
      customer_impacting: true,
      has_customer_contact: !!(details.customerEmail || details.customerPhone),
      amount: details.amount,
      currency: details.currency
    },
    extra: {
      paymentDetails: {
        ...details,
        // Ensure we capture contact info for manual follow-up
        customerContact: {
          email: details.customerEmail,
          phone: details.customerPhone
        }
      }
    },
    provider: {
      type: "payment",
      provider: details.provider,
      operation: "charge",
      providerErrorCode: details.errorCode
    }
  };
  provider.captureException(
    error instanceof Error ? error : new Error(`Payment failure: ${String(error)}`),
    context
  );
}
function captureAccessCodeFailure(error, details) {
  const provider = getProviderInstance();
  if (!provider) return;
  addBreadcrumb(
    "access_control",
    `Access code generation failed: ${details.provider}`,
    {
      provider: details.provider,
      bookingId: details.bookingId,
      facilityName: details.facilityName
    },
    "error"
  );
  const context = {
    level: "error",
    tags: {
      access_provider: details.provider,
      alert_priority: "critical",
      customer_impacting: true,
      requires_manual_action: true,
      booking_id: details.bookingId,
      facility: details.facilityName,
      has_customer_contact: !!(details.customerEmail || details.customerPhone)
    },
    extra: {
      accessCodeDetails: {
        ...details,
        // Ensure we capture all info needed for manual code generation
        customerContact: {
          email: details.customerEmail,
          phone: details.customerPhone
        },
        timeWindow: {
          start: details.startTime.toISOString(),
          end: details.endTime.toISOString(),
          durationMinutes: Math.round((details.endTime.getTime() - details.startTime.getTime()) / 6e4)
        }
      }
    },
    provider: {
      type: "access_control",
      provider: details.provider,
      operation: "create_code",
      providerErrorCode: details.errorCode
    }
  };
  provider.captureException(
    error instanceof Error ? error : new Error(`Access code generation failed: ${String(error)}`),
    context
  );
}
function captureEmailDeliveryFailure(error, details) {
  const provider = getProviderInstance();
  if (!provider) return;
  addBreadcrumb(
    "email",
    `Email delivery failure: ${details.kind}`,
    {
      source: details.source,
      kind: details.kind,
      recipient: obfuscateEmail(details.recipientEmail),
      notificationType: details.notificationType
    },
    "error"
  );
  const context = {
    level: "error",
    tags: {
      email_failure_kind: details.kind,
      email_failure_source: details.source,
      notification_type: details.notificationType ?? "unknown",
      // A failed invitation or password reset locks someone out of the product; an
      // optional reminder does not. Same event, different page-worthiness.
      alert_priority: details.transactional ? "critical" : "high",
      customer_impacting: true,
      ...details.smtpStatus ? { smtp_status: details.smtpStatus } : {}
    },
    extra: {
      emailDelivery: {
        ...details,
        // Raw, so whoever picks up the alert can actually reach this person.
        recipientEmail: details.recipientEmail
      }
    },
    provider: {
      type: "email",
      provider: "sendgrid",
      operation: details.source === "preflight" ? "suppression_check" : "delivery_event",
      providerErrorCode: details.smtpStatus
    }
  };
  provider.captureException(
    error instanceof Error ? error : new Error(`Email delivery failure: ${String(error)}`),
    context
  );
}

// core/logger.ts
var LOG_SEVERITY_WEIGHT = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};
var REDACTED_KEYS = /* @__PURE__ */ new Set([
  "password",
  "newpassword",
  "currentpassword",
  "token",
  "accesstoken",
  "refreshtoken",
  "authorization",
  "secret",
  // `isRedactedKey` matches the NORMALIZED key exactly, so `secret` alone does not cover a
  // prefixed one: `signingSecret` normalizes to `signingsecret`. These are the live HMAC keys
  // for outbound webhooks — logging an endpoint row wholesale would emit one to stdout and
  // into a Sentry breadcrumb. Add any future `<thing>Secret` here too.
  "signingsecret",
  "webhooksecret",
  "apikey",
  "clientsecret",
  "cardnumber",
  "cvv",
  "cvc",
  "ssn",
  "xsupabasetoken"
]);
var MAX_REDACTION_DEPTH = 4;
function isRedactedKey(key) {
  return REDACTED_KEYS.has(key.toLowerCase().replace(/[-_]/g, ""));
}
function redactLogData(data, depth = 0) {
  const out = {};
  for (const [key, value] of Object.entries(data)) {
    if (isRedactedKey(key)) {
      out[key] = "[redacted]";
      continue;
    }
    if (value !== null && typeof value === "object" && depth < MAX_REDACTION_DEPTH) {
      if (Array.isArray(value)) {
        out[key] = value.map(
          (item) => item !== null && typeof item === "object" && !Array.isArray(item) ? redactLogData(item, depth + 1) : item
        );
      } else if (value instanceof Date || value instanceof Error) {
        out[key] = value;
      } else {
        out[key] = redactLogData(value, depth + 1);
      }
      continue;
    }
    out[key] = value;
  }
  return out;
}
var breadcrumbSink = (entry) => {
  const breadcrumbLevel = entry.level === "warn" ? "warning" : entry.level;
  addBreadcrumb(entry.cat, entry.msg, { ...entry.context, ...entry.data }, breadcrumbLevel);
};
var sinks = [breadcrumbSink];
var contextSource = () => ({});
function configureLogger(options) {
  if (options.sinks) sinks = options.sinks;
  if (options.contextSource) contextSource = options.contextSource;
}
function emit(level, cat, msg, data) {
  const entry = {
    ts: (/* @__PURE__ */ new Date()).toISOString(),
    level,
    cat,
    msg,
    context: contextSource(),
    ...data && { data: redactLogData(data) }
  };
  for (const sink of sinks) {
    try {
      sink(entry);
    } catch {
    }
  }
  return entry;
}
var log = {
  /** Diagnostic chatter. Stdout only when LOG_LEVEL=debug; always breadcrumbed. */
  debug(cat, msg, data) {
    emit("debug", cat, msg, data);
  },
  /** Business events worth seeing live (charge attempted, booking created…). */
  info(cat, msg, data) {
    emit("info", cat, msg, data);
  },
  /** Recoverable anomalies: retries, fallbacks, access violations. */
  warn(cat, msg, data) {
    emit("warn", cat, msg, data);
  },
  /**
   * Failures. Capture stays at the boundary unless `{ capture: true }`
   * (fire-and-forget paths only — see capture policy).
   */
  error(cat, msg, data, options) {
    const entry = emit("error", cat, msg, data);
    if (options?.capture) {
      const provider = getProviderInstance();
      if (!provider) return;
      const err = options.error instanceof Error ? options.error : new Error(options.error !== void 0 ? String(options.error) : msg);
      provider.captureException(err, {
        level: options.level ?? "error",
        tags: { category: cat },
        extra: { ...entry.context, ...entry.data }
      });
    }
  }
};

// index.ts
function captureException(error, context) {
  const provider = getProviderInstance();
  if (!provider) {
    return;
  }
  provider.captureException(
    error instanceof Error ? error : new Error(String(error)),
    context
  );
}
function captureMessage(message, level, context) {
  const provider = getProviderInstance();
  if (!provider) {
    return;
  }
  provider.captureMessage(message, level || "info", context);
}
function setUser(user) {
  const provider = getProviderInstance();
  if (!provider) return;
  provider.setUser(user);
}
async function flush(timeout) {
  const provider = getProviderInstance();
  if (!provider) return false;
  return provider.flush(timeout);
}

export { LOG_SEVERITY_WEIGHT, breadcrumbSink, captureAccessCodeFailure, captureEmailDeliveryFailure, captureException, captureMessage, capturePaymentFailure, captureProviderError, captureSecurityEvent, configureLogger, flush, log, redactLogData, reportClientPaymentError, setUser };
//# sourceMappingURL=chunk-JWV2IHGA.js.map
//# sourceMappingURL=chunk-JWV2IHGA.js.map